import { useRef, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { clsx } from '../clsx';
import styles from './Tabs.module.css';

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** Counter after the label (tabular, subtle). */
  count?: string | number | undefined;
  disabled?: boolean | undefined;
}

export interface TabsProps<T extends string> extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: ReadonlyArray<TabItem<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Accessible name of the tab list. */
  label: string;
  /** Id prefix of the tab panels (`${panelId}-${value}`) for `aria-controls`. */
  panelId?: string | undefined;
  /** `line` draws the hairline under the whole list (Deals); `bare` only the active underline (Beratung, in-card). */
  variant?: 'line' | 'bare' | undefined;
}

/** Tab list with the 2 px accent underline; arrow keys move between tabs. */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  label,
  panelId,
  variant = 'line',
  className,
  ...rest
}: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const enabled = items.filter((item) => !item.disabled);
    const index = enabled.findIndex((item) => item.value === value);
    if (index < 0 || enabled.length === 0) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % enabled.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = enabled.length - 1;
    else return;
    event.preventDefault();
    const target = enabled[next];
    if (!target) return;
    onChange(target.value);
    listRef.current?.querySelector<HTMLElement>(`[data-value="${target.value}"]`)?.focus();
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={clsx(styles.list, variant === 'bare' && styles.bare, className)}
      onKeyDown={onKeyDown}
      {...rest}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            data-value={item.value}
            aria-selected={active}
            aria-controls={panelId ? `${panelId}-${item.value}` : undefined}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            className={clsx(styles.tab, active && styles.active)}
            onClick={() => onChange(item.value)}
          >
            {item.label}
            {item.count !== undefined ? <span className={styles.count}>{item.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
